import { useState, useEffect } from "react";
import { useAuth, apiClient, clearSessionToken, API } from "@/App";
import { useSearchParams, useNavigate } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { 
  User, MapPin, FileText, Link2, Shield, Loader2, Save, Check,
  Calendar, Car, Train, LogOut, Mail, Lock, Eye, EyeOff, Instagram, Facebook, Crown, Trash2, AlertTriangle, Users, Wrench, RotateCcw, Minus, Copy, Rss, Upload, Download, QrCode, Music, ChevronRight, ArrowLeft, Receipt, Globe
} from "lucide-react";
import { toast } from "sonner";
import { Layout } from "@/pages/Dashboard";
import { Kicker } from "@/design-system/primitives";
import { tokens } from "@/design-system/tokens";
import OnboardingCarousel from "@/pages/OnboardingCarousel";
import { PricingCard } from "@/components/Subscription";
import { CalendarImport } from "@/components/CalendarImport";
import { IcsImport } from "@/components/IcsImport";
import { ExportCard } from "@/components/ExportCard";
import { StationAutocomplete } from "@/components/StationAutocomplete";
import { RegularPlacesEditor } from "@/components/RegularPlacesEditor";
import { AdminVenueManager, AdminDuplicateDetector, AdminSmartFillTest, AdminSubscribers, AdminAnalytics, AdminGrantLifetime, AdminInvitationEmailPreview, AdminRecomputeTravelEstimates } from "@/components/AdminTools";
import { copyToClipboard } from "@/lib/clipboard";
import { QRCodeSVG } from "qrcode.react";

// X (Twitter) Logo SVG Component
const XLogo = ({ className }) => (
  <svg viewBox="0 0 24 24" className={className} fill="currentColor">
    <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
  </svg>
);

// ───── Integration row primitives (used in the Integrations tab) ──────
// Consolidated visual language so every integration card looks the same
// regardless of which third party it connects to. Single icon-tile style
// (innerBg + border.inner + textDim icon), single connected badge,
// single Connect CTA. Drops the 8 different brand-coloured icon tiles
// the previous design had.
// Embeddable public-gigs widget for a musician's own website. Primary option is
// a <script> embed (injects into the host DOM → inherits their site's fonts and
// carries schema.org Event data Google can read); a plain <iframe> is offered as
// a zero-setup fallback.
function WebsiteWidgetCard({ username }) {
  const [theme, setTheme] = useState("light");
  const [copiedJs, setCopiedJs] = useState(false);
  const [copiedFrame, setCopiedFrame] = useState(false);
  if (!username || username.length < 3) return null;

  const origin = window.location.origin;
  const jsSnippet =
    `<div data-fretless-gigs="${username}" data-accent="F5891F" data-limit="10"></div>\n` +
    `<script src="${origin}/gigs-widget.js" async></script>`;
  const previewSrc = `${origin}/embed/gigs/${username}?theme=${theme}`;
  const frameSnippet = `<iframe src="${previewSrc}" width="100%" height="600" style="border:none;" title="Upcoming gigs — Fretless" loading="lazy"></iframe>`;

  const copy = async (text, setFlag) => {
    await copyToClipboard(text);
    setFlag(true);
    toast.success("Embed code copied!");
    setTimeout(() => setFlag(false), 2000);
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Globe className="h-5 w-5" /> Website widget
        </CardTitle>
        <CardDescription>
          Show your public upcoming gigs on your own website — it updates automatically as you add
          gigs, and only public gigs appear. Paste this where you want the list to show.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="text-xs text-muted-foreground">
          Recommended — matches your site's fonts and is discoverable by Google (adds Event data to the page).
        </div>
        <pre className="text-xs bg-secondary/50 rounded-lg p-3 overflow-x-auto whitespace-pre-wrap break-all" data-testid="widget-embed-code">{jsSnippet}</pre>
        <Button
          type="button"
          size="sm"
          variant="outline"
          className="gap-1.5"
          data-testid="copy-widget-code-btn"
          onClick={() => copy(jsSnippet, setCopiedJs)}
        >
          {copiedJs ? <Check className="h-3.5 w-3.5" /> : <Copy className="h-3.5 w-3.5" />}
          {copiedJs ? "Copied" : "Copy embed code"}
        </Button>
        <p className="text-xs text-muted-foreground">
          Customise with <code>data-accent</code> (hex, no #), <code>data-limit</code>, and{" "}
          <code>data-heading</code>.
        </p>

        <div>
          <p className="text-xs text-muted-foreground mb-2">Preview</p>
          <iframe
            title="Widget preview"
            src={previewSrc}
            className="w-full border border-border rounded-xl bg-card"
            style={{ height: 320 }}
            data-testid="widget-preview"
          />
        </div>

        <details className="text-xs text-muted-foreground">
          <summary className="cursor-pointer select-none">Prefer a plain iframe? (simplest, but not Google-discoverable)</summary>
          <div className="mt-2 space-y-2">
            <div className="flex items-center gap-2">
              <span>Theme</span>
              <Button type="button" size="sm" variant={theme === "light" ? "default" : "outline"} onClick={() => setTheme("light")} data-testid="widget-theme-light">Light</Button>
              <Button type="button" size="sm" variant={theme === "dark" ? "default" : "outline"} onClick={() => setTheme("dark")} data-testid="widget-theme-dark">Dark</Button>
            </div>
            <pre className="bg-secondary/50 rounded-lg p-3 overflow-x-auto whitespace-pre-wrap break-all">{frameSnippet}</pre>
            <Button
              type="button"
              size="sm"
              variant="outline"
              className="gap-1.5"
              data-testid="copy-iframe-code-btn"
              onClick={() => copy(frameSnippet, setCopiedFrame)}
            >
              {copiedFrame ? <Check className="h-3.5 w-3.5" /> : <Copy className="h-3.5 w-3.5" />}
              {copiedFrame ? "Copied" : "Copy iframe code"}
            </Button>
          </div>
        </details>
      </CardContent>
    </Card>
  );
}

// "Refer musicians, earn Pro" — share link + progress toward the reward.
function ReferralCard({ username }) {
  const [stats, setStats] = useState(null);
  const [copied, setCopied] = useState(false);
  useEffect(() => {
    apiClient.get("/referrals/stats").then((r) => setStats(r.data)).catch(() => {});
  }, []);
  if (!username || username.length < 3) return null;

  const link = `${window.location.origin}/musician/${username}`;
  const threshold = stats?.reward_threshold || 6;
  const months = stats?.reward_months || 6;
  const progress = stats ? (stats.converted_count % threshold) : 0;

  const Stat = ({ label, value }) => (
    <div className="rounded-lg bg-secondary/50 py-3">
      <div className="text-2xl font-bold">{value}</div>
      <div className="text-xs text-muted-foreground">{label}</div>
    </div>
  );

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Crown className="h-5 w-5" /> Refer musicians, earn Pro
        </CardTitle>
        <CardDescription>
          Share Fretless. For every {threshold} musicians you refer who go Pro, you get {months} months of Pro, free.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid grid-cols-3 gap-2 text-center" data-testid="referral-stats">
          <Stat label="Referred" value={stats?.referred_count ?? "—"} />
          <Stat label="Went Pro" value={stats?.converted_count ?? "—"} />
          <Stat label="Rewards" value={stats?.rewards_earned ?? "—"} />
        </div>

        <div>
          <div className="flex justify-between text-xs text-muted-foreground mb-1">
            <span>Progress to next reward</span>
            <span>{progress}/{threshold} gone Pro</span>
          </div>
          <div className="h-2 rounded-full bg-secondary overflow-hidden">
            <div className="h-full bg-primary transition-all" style={{ width: `${Math.min(100, (progress / threshold) * 100)}%` }} />
          </div>
        </div>

        {stats?.comp_access_until && (
          <p className="text-xs text-muted-foreground">
            🎉 Reward Pro access active until{" "}
            {new Date(stats.comp_access_until).toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" })}.
          </p>
        )}

        <div className="flex items-center gap-2">
          <div className="flex-1 bg-secondary/50 rounded-lg px-3 py-2 text-xs font-mono truncate" data-testid="referral-link">{link}</div>
          <Button
            type="button"
            size="sm"
            variant="outline"
            className="shrink-0 gap-1.5"
            data-testid="copy-referral-link-btn"
            onClick={async () => {
              await copyToClipboard(link);
              setCopied(true);
              toast.success("Referral link copied!");
              setTimeout(() => setCopied(false), 2000);
            }}
          >
            {copied ? <Check className="h-3.5 w-3.5" /> : <Copy className="h-3.5 w-3.5" />}
            {copied ? "Copied" : "Copy"}
          </Button>
        </div>
        <p className="text-xs text-muted-foreground">
          Anyone who signs up after visiting your profile or scanning your QR counts as your referral. Rewards unlock once they upgrade to Pro.
        </p>
      </CardContent>
    </Card>
  );
}

function IntegrationRow({ icon: Icon, title, description, helpText, actions, children, connected = false, comingSoon = false, testId }) {
  const tk = tokens;
  const [helpOpen, setHelpOpen] = useState(false);
  return (
    <div
      data-testid={testId}
      style={{
        background: tk.color.cardBg,
        border: tk.border.card,
        borderRadius: tk.radius.lg,
        padding: tk.space.lg,
        opacity: comingSoon ? 0.55 : 1,
      }}
    >
      {/* Layout: icon column left (? help toggle sits under the icon), then
          a title-level line carrying title → Connected chip → buttons, with
          the description flowing underneath. minHeight 36 centres the title
          line against the 36px icon so icon, title and buttons all sit level;
          the chip+buttons cluster is pushed right (marginLeft auto), leaving
          the gap between title and buttons for "Connected" to occupy. */}
      <div style={{ display: "flex", alignItems: "flex-start", gap: tk.space.md }}>
        <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: tk.space.sm, flexShrink: 0 }}>
          <div
            style={{
              width: 36,
              height: 36,
              borderRadius: tk.radius.md,
              background: tk.color.innerBg,
              border: tk.border.inner,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <Icon style={{ width: 18, height: 18, color: tk.color.textDim }} />
          </div>
          {helpText && (
            <button
              type="button"
              onClick={() => setHelpOpen(o => !o)}
              aria-label="More info"
              aria-expanded={helpOpen}
              data-testid={testId ? `${testId}-help` : undefined}
              style={{
                width: 18,
                height: 18,
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                background: "transparent",
                color: helpOpen ? tk.color.orange : tk.color.textMuted,
                border: tk.border.inner,
                borderRadius: tk.radius.pill,
                cursor: "pointer",
                fontSize: 11,
                fontWeight: 700,
                fontFamily: tk.font.body,
                lineHeight: 1,
                flexShrink: 0,
                transition: `color ${tk.motion.fast}`,
              }}
              onMouseEnter={(e) => { if (!helpOpen) e.currentTarget.style.color = tk.color.text; }}
              onMouseLeave={(e) => { if (!helpOpen) e.currentTarget.style.color = tk.color.textMuted; }}
            >
              ?
            </button>
          )}
        </div>
        <div style={{ minWidth: 0, flex: 1 }}>
          <div style={{ display: "flex", alignItems: "center", flexWrap: "wrap", gap: tk.space.md, minHeight: 36 }}>
            <h4 style={{ margin: 0, ...tk.type.titleSm, color: tk.color.text }}>{title}</h4>
            {(connected || actions) && (
              <div
                className="flex flex-wrap items-center"
                style={{ gap: tk.space.sm, marginLeft: "auto" }}
              >
                {connected && <ConnectedBadge />}
                {actions}
              </div>
            )}
          </div>
          {description && (
            <p style={{ margin: 0, marginTop: 2, ...tk.type.bodySm, color: tk.color.textDim }}>{description}</p>
          )}
        </div>
      </div>
      {/* Bands, not boxes: help text and controls sit below a hairline (the
          design system's card-as-bands rule — no nested bordered insets). */}
      {helpOpen && helpText && (
        <div
          style={{
            marginTop: tk.space.md,
            paddingTop: tk.space.md,
            borderTop: tk.border.hairline,
            ...tk.type.bodySm,
            color: tk.color.textDim,
          }}
        >
          {helpText}
        </div>
      )}
      {children && (
        <div
          style={{
            marginTop: tk.space.md,
            paddingTop: tk.space.md,
            borderTop: tk.border.hairline,
          }}
        >
          {children}
        </div>
      )}
    </div>
  );
}

// Default bank-account picker for payment sync. Rendered inside an
// IntegrationRow's children box when a platform is connected. Marking an
// invoice paid records a payment against the chosen account on that platform.
function BankAccountPicker({ platform, accounts, value, onChange, testId }) {
  const tk = tokens;
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
      <label style={{ ...tk.type.bodySm, color: tk.color.text, fontWeight: 600 }}>
        Record payments to
      </label>
      <select
        value={value || ""}
        onChange={(e) => onChange(e.target.value)}
        data-testid={testId}
        style={{
          width: "100%",
          maxWidth: 320,
          height: 38,
          padding: "0 10px",
          background: tk.color.cardBg,
          border: tk.border.inner,
          borderRadius: tk.radius.md,
          color: tk.color.text,
          ...tk.type.bodySm,
        }}
      >
        <option value="">Select a bank account…</option>
        {accounts.map((a) => (
          <option key={a.id} value={a.id}>{a.name}</option>
        ))}
      </select>
      {/* No explainer sentence — the Accounting section intro already says
          payments record automatically when an invoice is marked paid. */}
    </div>
  );
}

// Section heading for grouping rows inside a tab — clearer than a kicker
// when you need a 'real' subhead. Bold, near-white, on the page background.
function SectionHeading({ children }) {
  return (
    <h2
      style={{
        margin: 0,
        ...tokens.type.titleMd,
        color: tokens.color.text,
      }}
    >
      {children}
    </h2>
  );
}

function ConnectedBadge() {
  return (
    <span
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 6,
        padding: "3px 8px",
        borderRadius: tokens.radius.pill,
        background: "rgba(34, 193, 151, 0.14)",
        color: tokens.color.green,
        fontFamily: tokens.font.mono,
        fontSize: 11,
        fontWeight: 600,
        letterSpacing: "0.08em",
        textTransform: "uppercase",
        whiteSpace: "nowrap",
      }}
    >
      <span style={{ width: 6, height: 6, borderRadius: tokens.radius.pill, background: tokens.color.green }} />
      Connected
    </span>
  );
}

function ComingSoonBadge() {
  return (
    <span
      style={{
        padding: "3px 8px",
        borderRadius: tokens.radius.pill,
        background: tokens.color.innerBg,
        border: tokens.border.inner,
        color: tokens.color.textMuted,
        fontFamily: tokens.font.mono,
        fontSize: 11,
        fontWeight: 600,
        letterSpacing: "0.08em",
        textTransform: "uppercase",
        whiteSpace: "nowrap",
      }}
    >
      Coming Soon
    </span>
  );
}

// Token-driven primary action button (Connect / Upload CSV / Scan / etc.).
function PrimaryAction({ onClick, loading, disabled, children, icon: Icon, testId }) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled || loading}
      data-testid={testId}
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 6,
        padding: "8px 14px",
        background: tokens.color.orange,
        color: tokens.color.onOrange,
        border: tokens.border.brand,
        borderRadius: tokens.radius.md + 1,
        fontFamily: tokens.font.body,
        fontSize: 13,
        fontWeight: 600,
        cursor: (disabled || loading) ? "not-allowed" : "pointer",
        opacity: (disabled || loading) ? 0.6 : 1,
        whiteSpace: "nowrap",
      }}
    >
      {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : (Icon && <Icon className="h-4 w-4" />)}
      {children}
    </button>
  );
}

// Token-driven ghost outline action button (Disconnect / Sync / Template).
function GhostAction({ onClick, loading, disabled, children, icon: Icon, testId, title }) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled || loading}
      data-testid={testId}
      title={title}
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 6,
        padding: "7px 12px",
        background: "transparent",
        color: tokens.color.textDim,
        border: tokens.border.card,
        borderRadius: tokens.radius.md + 1,
        fontFamily: tokens.font.body,
        fontSize: 13,
        fontWeight: 500,
        cursor: (disabled || loading) ? "not-allowed" : "pointer",
        opacity: (disabled || loading) ? 0.6 : 1,
        whiteSpace: "nowrap",
        transition: `color ${tokens.motion.fast}, border-color ${tokens.motion.fast}`,
      }}
      onMouseEnter={(e) => { if (!disabled && !loading) { e.currentTarget.style.color = tokens.color.text; e.currentTarget.style.borderColor = tokens.color.orangeLine; } }}
      onMouseLeave={(e) => { e.currentTarget.style.color = tokens.color.textDim; e.currentTarget.style.borderColor = tokens.color.cardBorder; }}
    >
      {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : (Icon && <Icon className="h-4 w-4" />)}
      {children}
    </button>
  );
}

export const Settings = () => {
  const { user, setUser, logout, checkAuth } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState("travel");
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [upgradedBanner, setUpgradedBanner] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [mobileShowContent, setMobileShowContent] = useState(false);
  const [deleteConfirmText, setDeleteConfirmText] = useState("");
  const [deleting, setDeleting] = useState(false);
  const [adminUsers, setAdminUsers] = useState([]);
  const [adminLoading, setAdminLoading] = useState(false);
  const [adminDeleteTarget, setAdminDeleteTarget] = useState(null);
  const [resettingPrompt, setResettingPrompt] = useState(false);
  const [adminResettingPrompt, setAdminResettingPrompt] = useState(null);
  const [usersListExpanded, setUsersListExpanded] = useState(false);
  const [selectedUsers, setSelectedUsers] = useState(() => new Set());
  const [showOnboarding, setShowOnboarding] = useState(false);
  
  // Travel settings
  const [travelSettings, setTravelSettings] = useState({
    home_address: "",
    home_city: "",
    home_postcode: "",
    home_country: "United Kingdom",
    home_station: "",  // legacy
    home_station_name: "",
    home_station_place_id: "",
    home_station_lat: null,
    home_station_lng: null,
    buffer_minutes: 30,
    default_transport_mode: "driving",
    apply_buffer_in_dashboard: true,
    mileage_expense_format: "amount",
    regular_places: []
  });
  
  // Invoice settings
  const [invoiceSettings, setInvoiceSettings] = useState({
    business_name: "",
    business_address: "",
    bank_name: "",
    account_number: "",
    sort_code: "",
    payment_terms: "Payment due within 14 days",
    invoice_notes: ""
  });
  
  // Google Calendar status
  const [calendarConnected, setCalendarConnected] = useState(false);
  // Which calendar Fretless writes gigs to (the one the user ticks for import)
  const [gigCalendars, setGigCalendars] = useState([]);
  const [gigCalendarId, setGigCalendarId] = useState("primary");

  useEffect(() => {
    if (!calendarConnected) return;
    apiClient.get("/calendar/list")
      .then(r => setGigCalendars((r.data.calendars || []).filter(c => c.id)))
      .catch(() => setGigCalendars([]));
  }, [calendarConnected]);

  const setSyncCalendar = async (id) => {
    try {
      await apiClient.put(`/calendar/settings?calendar_id=${encodeURIComponent(id)}`);
      setGigCalendarId(id);
      toast.success("Gig calendar updated. New and changed gigs will sync here.");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not update gig calendar");
    }
  };
  const [calendarLoading, setCalendarLoading] = useState(false);
  // FreeAgent status
  const [freeagentConnected, setFreeagentConnected] = useState(false);
  const [freeagentCompanyName, setFreeagentCompanyName] = useState("");
  const [freeagentLoading, setFreeagentLoading] = useState(false);
  // Xero status
  const [xeroConnected, setXeroConnected] = useState(false);
  const [xeroCompanyName, setXeroCompanyName] = useState("");
  const [xeroLoading, setXeroLoading] = useState(false);
  // QuickBooks status
  const [quickbooksConnected, setQuickbooksConnected] = useState(false);
  const [quickbooksCompanyName, setQuickbooksCompanyName] = useState("");
  const [quickbooksLoading, setQuickbooksLoading] = useState(false);
  // Default bank accounts for payment sync (mark-paid → record payment)
  const [freeagentBankAccounts, setFreeagentBankAccounts] = useState([]);
  const [freeagentDefaultBank, setFreeagentDefaultBank] = useState("");
  const [xeroBankAccounts, setXeroBankAccounts] = useState([]);
  const [xeroDefaultBank, setXeroDefaultBank] = useState("");
  const [xeroBankError, setXeroBankError] = useState("");
  const [quickbooksBankAccounts, setQuickbooksBankAccounts] = useState([]);
  const [quickbooksDefaultBank, setQuickbooksDefaultBank] = useState("");
  const [quickbooksBankError, setQuickbooksBankError] = useState("");
  const [calendarIncludeTravel, setCalendarIncludeTravel] = useState(false);
  const [feedToken, setFeedToken] = useState(null);
  const [feedCopied, setFeedCopied] = useState(false);
  
  // Password change
  const [passwordData, setPasswordData] = useState({
    current: "",
    new: "",
    confirm: ""
  });
  const [showPasswords, setShowPasswords] = useState(false);
  
  // Social handles and style examples for "My Style" AI tone
  const [socialHandles, setSocialHandles] = useState({
    instagram_handle: "",
    twitter_handle: "",
    facebook_handle: ""
  });
  const [artistNames, setArtistNames] = useState("");
  const [website, setWebsite] = useState("");
  const [username, setUsername] = useState("");
  const [usernameSaving, setUsernameSaving] = useState(false);
  const [profileLinkCopied, setProfileLinkCopied] = useState(false);
  const [songkickUrl, setSongkickUrl] = useState("");
  const [songkickLoading, setSongkickLoading] = useState(false);
  const [songkickResults, setSongkickResults] = useState(null);
  const [csvLoading, setCsvLoading] = useState(false);
  const [csvPreview, setCsvPreview] = useState(null);
  const [csvSelected, setCsvSelected] = useState(new Set());
  const [styleExamples, setStyleExamples] = useState([]);
  const [newExample, setNewExample] = useState("");
  
  useEffect(() => {
    fetchSettings();
  }, []);

  // Handle ?upgraded=true after Stripe success
  useEffect(() => {
    if (searchParams.get("upgraded") === "true") {
      setUpgradedBanner(true);
      setActiveTab("plan");
      checkAuth();
      searchParams.delete("upgraded");
      setSearchParams(searchParams, { replace: true });
    }
    // Handle ?tab=<name> — used by onboarding carousel and session-expiry redirect
    const tabParam = searchParams.get("tab");
    const validTabs = ["travel", "invoice", "plan", "integrations", "account", "profile"];
    if (tabParam && validTabs.includes(tabParam)) {
      setActiveTab(tabParam);
      setMobileShowContent(true);
      searchParams.delete("tab");
      setSearchParams(searchParams, { replace: true });
    }
    // Handle ?calendar_connected=true after Google Calendar OAuth
    if (searchParams.get("calendar_connected") === "true") {
      setActiveTab("integrations");
      setCalendarConnected(true);
      setMobileShowContent(true);
      searchParams.delete("calendar_connected");
      setSearchParams(searchParams, { replace: true });
    }
    // Handle ?freeagent_connected=true after FreeAgent OAuth
    if (searchParams.get("freeagent_connected") === "true") {
      toast.success("Connected to FreeAgent");
      setActiveTab("integrations");
      setFreeagentConnected(true);
      setMobileShowContent(true);
      searchParams.delete("freeagent_connected");
      setSearchParams(searchParams, { replace: true });
    }
    if (searchParams.get("freeagent_error")) {
      toast.error("FreeAgent connection failed");
      searchParams.delete("freeagent_error");
      setSearchParams(searchParams, { replace: true });
    }
    // Handle ?xero_connected=true after Xero OAuth
    if (searchParams.get("xero_connected") === "true") {
      toast.success("Connected to Xero");
      setActiveTab("integrations");
      setXeroConnected(true);
      setMobileShowContent(true);
      searchParams.delete("xero_connected");
      setSearchParams(searchParams, { replace: true });
    }
    if (searchParams.get("xero_error")) {
      toast.error("Xero connection failed");
      searchParams.delete("xero_error");
      setSearchParams(searchParams, { replace: true });
    }
    // Handle ?quickbooks_connected=true after QuickBooks OAuth
    if (searchParams.get("quickbooks_connected") === "true") {
      toast.success("Connected to QuickBooks");
      setActiveTab("integrations");
      setQuickbooksConnected(true);
      setMobileShowContent(true);
      searchParams.delete("quickbooks_connected");
      setSearchParams(searchParams, { replace: true });
    }
    if (searchParams.get("quickbooks_error")) {
      toast.error("QuickBooks connection failed");
      searchParams.delete("quickbooks_error");
      setSearchParams(searchParams, { replace: true });
    }
  }, [searchParams, setSearchParams, checkAuth]);
  
  const fetchSettings = async () => {
    setLoading(true);
    try {
      const [travelRes, invoiceRes, calendarRes, profileRes, freeagentRes, xeroRes, quickbooksRes] = await Promise.all([
        apiClient.get("/user/travel-settings").catch(() => ({ data: {} })),
        apiClient.get("/invoice-settings").catch(() => ({ data: {} })),
        apiClient.get("/calendar/status").catch(() => ({ data: { connected: false } })),
        apiClient.get("/user/profile").catch(() => ({ data: {} })),
        apiClient.get("/accounting/freeagent/status").catch(() => ({ data: { connected: false } })),
        apiClient.get("/accounting/xero/status").catch(() => ({ data: { connected: false } })),
        apiClient.get("/accounting/quickbooks/status").catch(() => ({ data: { connected: false } }))
      ]);

      setFreeagentConnected(freeagentRes.data?.connected || false);
      setFreeagentCompanyName(freeagentRes.data?.company_name || "");
      setFreeagentDefaultBank(freeagentRes.data?.default_bank_account || "");
      setXeroConnected(xeroRes.data?.connected || false);
      setXeroCompanyName(xeroRes.data?.company_name || "");
      setXeroDefaultBank(xeroRes.data?.default_bank_account || "");
      setQuickbooksConnected(quickbooksRes.data?.connected || false);
      setQuickbooksCompanyName(quickbooksRes.data?.company_name || "");
      setQuickbooksDefaultBank(quickbooksRes.data?.default_bank_account || "");

      // Load bank-account options for connected platforms (for the payment-sync picker)
      if (freeagentRes.data?.connected) loadFreeagentBankAccounts();
      if (xeroRes.data?.connected) loadXeroBankAccounts();
      if (quickbooksRes.data?.connected) loadQuickbooksBankAccounts();
      
      if (travelRes.data && Object.keys(travelRes.data).length > 0) {
        setTravelSettings(prev => ({ ...prev, ...travelRes.data }));
      }
      
      if (invoiceRes.data && Object.keys(invoiceRes.data).length > 0) {
        setInvoiceSettings(prev => ({ ...prev, ...invoiceRes.data }));
      }
      
      if (profileRes.data) {
        setSocialHandles({
          instagram_handle: profileRes.data.instagram_handle || "",
          twitter_handle: profileRes.data.twitter_handle || "",
          facebook_handle: profileRes.data.facebook_handle || ""
        });
        setArtistNames(profileRes.data.artist_names || "");
        setWebsite(profileRes.data.website || "");
        setStyleExamples(profileRes.data.style_examples || []);
      }
      
      // Load username from user doc — auto-generate one from artist/display name if missing
      try {
        const meRes = await apiClient.get("/auth/me");
        let loadedUsername = meRes.data?.username || "";
        if (!loadedUsername) {
          try {
            const genRes = await apiClient.post("/user/username/auto-generate");
            loadedUsername = genRes.data?.username || "";
          } catch {}
        }
        setUsername(loadedUsername);
      } catch {}
      
      setCalendarConnected(calendarRes.data?.connected || false);
      setCalendarIncludeTravel(calendarRes.data?.include_travel_in_calendar || false);
      setGigCalendarId(calendarRes.data?.calendar_id || "primary");
      
      // Load iCal feed token
      try {
        const feedRes = await apiClient.post("/calendar/feed/generate");
        setFeedToken(feedRes.data?.feed_token || null);
      } catch {}
    } catch (error) {
      console.error("Failed to load settings:", error);
    } finally {
      setLoading(false);
    }
  };
  
  const saveTravelSettings = async () => {
    setSaving(true);
    try {
      const res = await apiClient.put("/user/travel-settings", travelSettings);
      // The backend fills in home → place miles for newly added regular places.
      setTravelSettings(prev => ({ ...prev, regular_places: res.data?.regular_places || prev.regular_places }));
      toast.success("Travel settings saved!");
    } catch (error) {
      const msg = error.response?.data?.detail || error.message || "Unknown error";
      console.error("Failed to save travel settings:", msg);
      toast.error(`Failed to save travel settings: ${msg}`);
    } finally {
      setSaving(false);
    }
  };
  
  const saveInvoiceSettings = async () => {
    setSaving(true);
    try {
      await apiClient.post("/invoice-settings", invoiceSettings);
      toast.success("Invoice settings saved!");
    } catch (error) {
      toast.error("Failed to save invoice settings");
    } finally {
      setSaving(false);
    }
  };
  
  const connectGoogleCalendar = async () => {
    setCalendarLoading(true);
    try {
      const response = await apiClient.get("/calendar/auth-url");
      if (response.data.auth_url) {
        window.location.href = response.data.auth_url;
      }
    } catch (error) {
      toast.error("Failed to connect Google Calendar");
      setCalendarLoading(false);
    }
  };
  
  const disconnectGoogleCalendar = async () => {
    setCalendarLoading(true);
    try {
      await apiClient.post("/calendar/disconnect");
      setCalendarConnected(false);
      toast.success("Google Calendar disconnected");
    } catch (error) {
      toast.error("Failed to disconnect");
    } finally {
      setCalendarLoading(false);
    }
  };

  const connectFreeagent = async () => {
    setFreeagentLoading(true);
    try {
      const { data } = await apiClient.get("/accounting/freeagent/auth-url");
      if (data.auth_url) {
        window.open(data.auth_url, "_blank", "noopener,noreferrer");
        toast.info("Complete the connection in the new tab, then refresh this page.");
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to start FreeAgent connection");
    } finally {
      setFreeagentLoading(false);
    }
  };

  const disconnectFreeagent = async () => {
    setFreeagentLoading(true);
    try {
      await apiClient.delete("/accounting/freeagent/disconnect");
      setFreeagentConnected(false);
      setFreeagentCompanyName("");
      setFreeagentBankAccounts([]);
      setFreeagentDefaultBank("");
      toast.success("FreeAgent disconnected");
    } catch (error) {
      toast.error("Failed to disconnect FreeAgent");
    } finally {
      setFreeagentLoading(false);
    }
  };

  // ── Payment-sync bank accounts ──────────────────────────────────────────────
  const loadFreeagentBankAccounts = async () => {
    try {
      const { data } = await apiClient.get("/accounting/freeagent/bank-accounts");
      setFreeagentBankAccounts(data.accounts || []);
      if (data.default_id) setFreeagentDefaultBank(data.default_id);
    } catch {
      setFreeagentBankAccounts([]);
    }
  };

  const loadXeroBankAccounts = async () => {
    setXeroBankError("");
    try {
      const { data } = await apiClient.get("/accounting/xero/bank-accounts");
      setXeroBankAccounts(data.accounts || []);
      if (data.default_id) setXeroDefaultBank(data.default_id);
    } catch (error) {
      setXeroBankAccounts([]);
      if (error.response?.status === 403) {
        setXeroBankError(error.response.data?.detail || "Reconnect Xero to enable payment sync.");
      }
    }
  };

  const saveDefaultBank = async (platform, bankAccountId) => {
    if (platform === "freeagent") setFreeagentDefaultBank(bankAccountId);
    else if (platform === "quickbooks") setQuickbooksDefaultBank(bankAccountId);
    else setXeroDefaultBank(bankAccountId);
    if (!bankAccountId) return;
    try {
      await apiClient.put(`/accounting/${platform}/default-bank-account`, { bank_account_id: bankAccountId });
      toast.success("Default bank account saved");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save bank account");
    }
  };

  const connectXero = async () => {
    setXeroLoading(true);
    try {
      const { data } = await apiClient.get("/accounting/xero/auth-url");
      if (data.auth_url) {
        window.open(data.auth_url, "_blank", "noopener,noreferrer");
        toast.info("Complete the connection in the new tab, then refresh this page.");
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to start Xero connection");
    } finally {
      setXeroLoading(false);
    }
  };

  const disconnectXero = async () => {
    setXeroLoading(true);
    try {
      await apiClient.delete("/accounting/xero/disconnect");
      setXeroConnected(false);
      setXeroCompanyName("");
      setXeroBankAccounts([]);
      setXeroDefaultBank("");
      setXeroBankError("");
      toast.success("Xero disconnected");
    } catch (error) {
      toast.error("Failed to disconnect Xero");
    } finally {
      setXeroLoading(false);
    }
  };

  const loadQuickbooksBankAccounts = async () => {
    setQuickbooksBankError("");
    try {
      const { data } = await apiClient.get("/accounting/quickbooks/bank-accounts");
      setQuickbooksBankAccounts(data.accounts || []);
      if (data.default_id) setQuickbooksDefaultBank(data.default_id);
    } catch (error) {
      setQuickbooksBankAccounts([]);
      if (error.response?.status === 403) {
        setQuickbooksBankError(error.response.data?.detail || "Reconnect QuickBooks to enable payment sync.");
      }
    }
  };

  const connectQuickbooks = async () => {
    setQuickbooksLoading(true);
    try {
      const { data } = await apiClient.get("/accounting/quickbooks/auth-url");
      if (data.auth_url) {
        window.open(data.auth_url, "_blank", "noopener,noreferrer");
        toast.info("Complete the connection in the new tab, then refresh this page.");
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to start QuickBooks connection");
    } finally {
      setQuickbooksLoading(false);
    }
  };

  const disconnectQuickbooks = async () => {
    setQuickbooksLoading(true);
    try {
      await apiClient.delete("/accounting/quickbooks/disconnect");
      setQuickbooksConnected(false);
      setQuickbooksCompanyName("");
      setQuickbooksBankAccounts([]);
      setQuickbooksDefaultBank("");
      setQuickbooksBankError("");
      toast.success("QuickBooks disconnected");
    } catch (error) {
      toast.error("Failed to disconnect QuickBooks");
    } finally {
      setQuickbooksLoading(false);
    }
  };

  const changePassword = async (e) => {
    e.preventDefault();
    
    if (passwordData.new !== passwordData.confirm) {
      toast.error("New passwords don't match");
      return;
    }
    
    if (passwordData.new.length < 6) {
      toast.error("Password must be at least 6 characters");
      return;
    }
    
    setSaving(true);
    try {
      await apiClient.post("/auth/change-password", {
        current_password: passwordData.current,
        new_password: passwordData.new
      });
      toast.success("Password changed successfully!");
      setPasswordData({ current: "", new: "", confirm: "" });
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to change password");
    } finally {
      setSaving(false);
    }
  };
  
  const saveSocialHandles = async () => {
    setSaving(true);
    try {
      await apiClient.put("/user/profile", {
        ...socialHandles,
        artist_names: artistNames,
        website: website,
        style_examples: styleExamples
      });
      toast.success("Profile saved!");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save profile");
    } finally {
      setSaving(false);
    }
  };
  
  const addStyleExample = () => {
    if (newExample.trim() && styleExamples.length < 5) {
      setStyleExamples([...styleExamples, newExample.trim()]);
      setNewExample("");
    }
  };
  
  const removeStyleExample = (index) => {
    setStyleExamples(styleExamples.filter((_, i) => i !== index));
  };

  const navigate = useNavigate();

  const handleDeleteAccount = async () => {
    if (deleteConfirmText !== "DELETE") return;
    setDeleting(true);
    try {
      await apiClient.delete("/account");
      clearSessionToken();
      toast.success("Your account has been deleted.");
      window.location.href = "/?deleted=true";
    } catch (error) {
      const status = error.response?.status;
      const detail = error.response?.data?.detail || "";

      if (status === 401) {
        clearSessionToken();
        // Distinguish session expiry from other auth errors
        if (detail.toLowerCase().includes("expired")) {
          toast.error("Your session has expired. Please sign in again.");
        } else {
          toast.error("Please sign in again to delete your account.");
        }
        // Redirect to landing with return intent
        navigate("/?returnTo=settings-account");
      } else {
        toast.error(detail || "Failed to delete account");
      }
    } finally {
      setDeleting(false);
    }
  };

  const fetchAdminUsers = async () => {
    if (!user?.is_admin) return;
    setAdminLoading(true);
    try {
      const res = await apiClient.get("/admin/users");
      setAdminUsers(res.data || []);
    } catch {
      // Not admin or error
    } finally {
      setAdminLoading(false);
    }
  };

  const handleAdminDeleteUser = async (targetUserId, targetEmail) => {
    try {
      await apiClient.delete(`/admin/users/${targetUserId}`);
      toast.success(`Account ${targetEmail} deleted`);
      setAdminDeleteTarget(null);
      fetchAdminUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to delete user");
    }
  };

  const handleResetInstallPrompt = async () => {
    setResettingPrompt(true);
    try {
      await apiClient.post("/user/reset-install-prompt");
      toast.success("Install prompt reset — it will show again on your next login");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to reset install prompt");
    } finally {
      setResettingPrompt(false);
    }
  };

  const handleAdminResetPrompt = async (targetUserId, targetEmail) => {
    setAdminResettingPrompt(targetUserId);
    try {
      const res = await apiClient.post(`/admin/users/${targetUserId}/reset-install-prompt`);
      toast.success(`Install prompt reset for ${targetEmail}`);
      setAdminUsers(prev => prev.map(u => 
        u.user_id === targetUserId ? { ...u, has_seen_install_prompt: false } : u
      ));
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to reset prompt");
    } finally {
      setAdminResettingPrompt(null);
    }
  };

  // Multi-select handlers
  const toggleUserSelection = (userId) => {
    setSelectedUsers((prev) => {
      const next = new Set(prev);
      next.has(userId) ? next.delete(userId) : next.add(userId);
      return next;
    });
  };

  const handleSelectAllUsers = (checked) => {
    if (checked) {
      setSelectedUsers(new Set(adminUsers.map(u => u.user_id)));
    } else {
      setSelectedUsers(new Set());
    }
  };

  const handleBulkResetPrompt = async () => {
    if (selectedUsers.size === 0) return;
    const targetUsers = adminUsers.filter(u => selectedUsers.has(u.user_id));
    try {
      for (const user of targetUsers) {
        await apiClient.post(`/admin/users/${user.user_id}/reset-install-prompt`);
      }
      toast.success(`Install prompt reset for ${targetUsers.length} user(s)`);
      setAdminUsers(prev => prev.map(u =>
        selectedUsers.has(u.user_id) ? { ...u, has_seen_install_prompt: false } : u
      ));
      setSelectedUsers(new Set());
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to reset prompts");
    }
  };

  const handleBulkDeleteUsers = async () => {
    if (selectedUsers.size === 0) return;
    const confirmMsg = `Delete ${selectedUsers.size} user(s)? This will also send GDPR confirmation emails to them.`;
    if (!window.confirm(confirmMsg)) return;

    const targetUsers = adminUsers.filter(u => selectedUsers.has(u.user_id));
    try {
      for (const user of targetUsers) {
        await apiClient.delete(`/admin/users/${user.user_id}`);
      }
      toast.success(`${targetUsers.length} user(s) deleted`);
      setSelectedUsers(new Set());
      fetchAdminUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to delete users");
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center min-h-[400px]">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
      </Layout>
    );
  }
  
  const navItems = [
    { value: "travel", label: "Travel", icon: MapPin },
    { value: "invoice", label: "Invoice", icon: FileText },
    { value: "plan", label: "Plan", icon: Crown },
    { value: "integrations", label: "Integrations", icon: Link2 },
    { value: "account", label: "Privacy", icon: Shield },
    { value: "profile", label: "Account", icon: User },
    ...(user?.is_admin ? [{ value: "admin", label: "Admin", icon: Users }] : []),
  ];

  // Mobile: show list or content

  const handleNavClick = (value) => {
    setActiveTab(value);
    setMobileShowContent(true);
    if (value === "admin") fetchAdminUsers();
  };

  const handleMobileBack = () => {
    setMobileShowContent(false);
  };

  // Render the content panel for the active tab
  const renderContent = () => (
    <>
      {activeTab === "travel" && (
          <Card>
            <CardHeader>
              <CardTitle>Travel Settings</CardTitle>
              <CardDescription>
                Set your home address to calculate journey times and "leave by" reminders
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="grid gap-4 md:grid-cols-2">
                <div className="space-y-2">
                  <Label htmlFor="home_address">Home Address</Label>
                  <Input
                    id="home_address"
                    value={travelSettings.home_address}
                    onChange={(e) => setTravelSettings({ ...travelSettings, home_address: e.target.value })}
                    data-testid="home-address"
                  />
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="home_city">City</Label>
                  <Input
                    id="home_city"
                    value={travelSettings.home_city}
                    onChange={(e) => setTravelSettings({ ...travelSettings, home_city: e.target.value })}
                    data-testid="home-city"
                  />
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="home_postcode">Postcode</Label>
                  <Input
                    id="home_postcode"
                    value={travelSettings.home_postcode}
                    onChange={(e) => setTravelSettings({ ...travelSettings, home_postcode: e.target.value })}
                    data-testid="home-postcode"
                  />
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="home_country">Country</Label>
                  <Input
                    id="home_country"
                    value={travelSettings.home_country}
                    onChange={(e) => setTravelSettings({ ...travelSettings, home_country: e.target.value })}
                    data-testid="home-country"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label className="flex items-center gap-2">
                  <Train className="h-4 w-4" />
                  Nearest Railway Station
                </Label>
                <p className="text-xs text-muted-foreground">
                  When your default transport is Transit, journeys are calculated as
                  drive to this station + the actual next train departure. Leave blank
                  to use end-to-end public transport from home.
                </p>
                <StationAutocomplete
                  value={
                    travelSettings.home_station_name
                      ? {
                          name: travelSettings.home_station_name,
                          place_id: travelSettings.home_station_place_id,
                          lat: travelSettings.home_station_lat,
                          lng: travelSettings.home_station_lng,
                        }
                      : null
                  }
                  onChange={(next) => setTravelSettings({
                    ...travelSettings,
                    home_station_name: next?.name || "",
                    home_station_place_id: next?.place_id || "",
                    home_station_lat: next?.lat ?? null,
                    home_station_lng: next?.lng ?? null,
                    // Keep legacy field in sync for back-compat
                    home_station: next?.name || "",
                  })}
                  placeholder="e.g. Reading"
                  testId="home-station"
                />
              </div>

              <div className="space-y-2">
                <Label className="flex items-center gap-2">
                  <MapPin className="h-4 w-4" />
                  Regular mileage
                </Label>
                <p className="text-xs text-muted-foreground">
                  Places you drive to that aren't the gig itself, like the station you park at.
                  Pick them in a gig's Transport section to log home ↔ place mileage.
                </p>
                <RegularPlacesEditor
                  places={travelSettings.regular_places || []}
                  onChange={(regular_places) => setTravelSettings(prev => ({ ...prev, regular_places }))}
                />
              </div>

              <div className="grid gap-4 md:grid-cols-2">
                <div className="space-y-2">
                  <Label htmlFor="buffer_minutes">Buffer Time (minutes)</Label>
                  <p className="text-xs text-muted-foreground">
                    Extra time added to journey for parking, setup, etc.
                  </p>
                  <Input
                    id="buffer_minutes"
                    type="number"
                    min="0"
                    max="120"
                    value={travelSettings.buffer_minutes}
                    onChange={(e) => setTravelSettings({ ...travelSettings, buffer_minutes: parseInt(e.target.value) || 0 })}
                    data-testid="buffer-minutes"
                  />
                </div>
                
                <div className="space-y-2">
                  <Label>Default Transport</Label>
                  <p className="text-xs text-muted-foreground">
                    Your usual way of getting to gigs
                  </p>
                  <div className="flex gap-2">
                    <Button
                      type="button"
                      variant={travelSettings.default_transport_mode === "driving" ? "default" : "outline"}
                      className="flex-1"
                      onClick={() => setTravelSettings({ ...travelSettings, default_transport_mode: "driving" })}
                    >
                      <Car className="h-4 w-4 mr-2" />
                      Driving
                    </Button>
                    <Button
                      type="button"
                      variant={travelSettings.default_transport_mode === "transit" ? "default" : "outline"}
                      className="flex-1"
                      onClick={() => setTravelSettings({ ...travelSettings, default_transport_mode: "transit" })}
                    >
                      <Train className="h-4 w-4 mr-2" />
                      Transit
                    </Button>
                  </div>
                </div>
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="mileage_expense_format">Mileage expense format</Label>
                <p className="text-xs text-muted-foreground">
                  Controls what gets added to gig expenses when you use the mileage calculator.
                </p>
                <select
                  id="mileage_expense_format"
                  value={travelSettings.mileage_expense_format}
                  onChange={(e) => setTravelSettings({ ...travelSettings, mileage_expense_format: e.target.value })}
                  className="w-full h-10 rounded-md border border-input bg-background px-3 py-2 text-sm"
                  data-testid="mileage-expense-format"
                >
                  <option value="amount">£ Amount — HMRC rate × miles (for most apps)</option>
                  <option value="miles">Miles only — for apps that calculate HMRC rate themselves (e.g. FreeAgent)</option>
                </select>
              </div>

              <Button onClick={saveTravelSettings} disabled={saving} data-testid="save-travel">
                {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}
                Save Travel Settings
              </Button>
            </CardContent>
          </Card>
        )}
        
        {/* INVOICE SETTINGS */}
        {activeTab === "invoice" && (
          <Card>
            <CardHeader>
              <CardTitle>Invoice Settings</CardTitle>
              <CardDescription>
                Your business details for generating invoices
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="grid gap-4 md:grid-cols-2">
                <div className="space-y-2">
                  <Label htmlFor="business_name">Business Name</Label>
                  <Input
                    id="business_name"
                    value={invoiceSettings.business_name}
                    onChange={(e) => setInvoiceSettings({ ...invoiceSettings, business_name: e.target.value })}
                    data-testid="business-name"
                  />
                </div>
                
                <div className="space-y-2 md:col-span-2">
                  <Label htmlFor="business_address">Business Address</Label>
                  <Textarea
                    id="business_address"
                    value={invoiceSettings.business_address}
                    onChange={(e) => setInvoiceSettings({ ...invoiceSettings, business_address: e.target.value })}
                    data-testid="business-address"
                  />
                </div>
              </div>
              
              <div className="border-t pt-4">
                <h4 className="font-medium mb-4">Bank Details</h4>
                <div className="grid gap-4 md:grid-cols-3">
                  <div className="space-y-2">
                    <Label htmlFor="bank_name">Bank Name</Label>
                    <Input
                      id="bank_name"
                      value={invoiceSettings.bank_name}
                      onChange={(e) => setInvoiceSettings({ ...invoiceSettings, bank_name: e.target.value })}
                      data-testid="bank-name"
                    />
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="sort_code">Sort Code</Label>
                    <Input
                      id="sort_code"
                      value={invoiceSettings.sort_code}
                      onChange={(e) => setInvoiceSettings({ ...invoiceSettings, sort_code: e.target.value })}
                      data-testid="sort-code"
                    />
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="account_number">Account Number</Label>
                    <Input
                      id="account_number"
                      value={invoiceSettings.account_number}
                      onChange={(e) => setInvoiceSettings({ ...invoiceSettings, account_number: e.target.value })}
                      data-testid="account-number"
                    />
                  </div>
                </div>
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="payment_terms">Payment Terms</Label>
                <Input
                  id="payment_terms"
                  value={invoiceSettings.payment_terms}
                  onChange={(e) => setInvoiceSettings({ ...invoiceSettings, payment_terms: e.target.value })}
                  data-testid="payment-terms"
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="invoice_notes">Default Invoice Notes</Label>
                <Textarea
                  id="invoice_notes"
                  value={invoiceSettings.invoice_notes}
                  onChange={(e) => setInvoiceSettings({ ...invoiceSettings, invoice_notes: e.target.value })}
                  data-testid="invoice-notes"
                />
              </div>
              
              <Button onClick={saveInvoiceSettings} disabled={saving} data-testid="save-invoice">
                {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}
                Save Invoice Settings
              </Button>
            </CardContent>
          </Card>
        )}
        
        {/* INTEGRATIONS — consolidated visual language. One row pattern,
            one icon-tile style, one Connect CTA, one Connected badge. */}
        {activeTab === "integrations" && (
          <div style={{ display: "flex", flexDirection: "column", gap: tokens.space.xxxl }}>
            {/* ── CALENDAR ── */}
            {/* One heading, one column — the old three-layer stack (section
                heading + "Google Integrations"/"Calendar Feed" kickers + row
                titles) said everything twice before a single control appeared. */}
            <div style={{ display: "flex", flexDirection: "column", gap: tokens.space.md }}>
              <SectionHeading>Calendar</SectionHeading>
              <IntegrationRow
                  icon={Calendar}
                  title="Google Calendar"
                  helpText="Auto-syncs your Fretless gigs to your Google Calendar whenever they're added or changed."
                  testId="google-calendar-section"
                  connected={calendarConnected}
                  actions={calendarConnected ? (
                    <>
                      <GhostAction
                        onClick={async () => {
                          setCalendarLoading(true);
                          try {
                            const r = await apiClient.post("/calendar/sync-all");
                            if (r.data.synced > 0) toast.success(`Synced ${r.data.synced} gig(s)`);
                            if (r.data.failed > 0) toast.warning(`${r.data.failed} failed`);
                          } catch (e) { toast.error(e.response?.data?.detail || "Sync failed"); }
                          finally { setCalendarLoading(false); }
                        }}
                        loading={calendarLoading}
                        icon={Calendar}
                        testId="sync-all-calendar"
                      >
                        Sync All
                      </GhostAction>
                      <GhostAction onClick={disconnectGoogleCalendar} loading={calendarLoading} testId="disconnect-calendar">
                        Disconnect
                      </GhostAction>
                    </>
                  ) : (
                    <PrimaryAction onClick={connectGoogleCalendar} loading={calendarLoading} icon={Link2} testId="connect-calendar">
                      Connect
                    </PrimaryAction>
                  )}
                >
                  {calendarConnected && gigCalendars.length > 0 && (
                    <div data-testid="gig-calendar-picker" style={{ display: "flex", flexDirection: "column", gap: tokens.space.xs, marginTop: tokens.space.sm }}>
                      <label htmlFor="gig-calendar-select" style={{ fontSize: "12px", fontWeight: 500, color: tokens.color.text }}>
                        Gig calendar
                      </label>
                      <p style={{ fontSize: "11px", color: tokens.color.textMuted }}>
                        Gigs you add or change sync to this calendar. Personal events, like family plans, stay out, so tick this same calendar when importing.
                      </p>
                      <select
                        id="gig-calendar-select"
                        value={gigCalendarId === "primary" ? (gigCalendars.find(c => c.primary)?.id || "primary") : gigCalendarId}
                        onChange={e => setSyncCalendar(e.target.value)}
                        style={{
                          fontSize: "12px",
                          padding: `${tokens.space.xs}px ${tokens.space.sm}px`,
                          borderRadius: tokens.radius.md,
                          border: `1px solid ${tokens.color.innerBorder}`,
                          backgroundColor: tokens.color.innerBg,
                          color: tokens.color.text,
                          maxWidth: "100%",
                        }}
                      >
                        {gigCalendars.map(c => (
                          <option key={c.id} value={c.id}>
                            {c.summary}{c.primary ? " (main)" : ""}
                          </option>
                        ))}
                      </select>
                    </div>
                  )}
                </IntegrationRow>

                {calendarConnected && (
                  <IntegrationRow
                    icon={Calendar}
                    title="Import Google Calendar gigs"
                    helpText="Scans your connected calendars for potential gigs to pull into Fretless — you preview and pick before anything is imported."
                    testId="calendar-import-section"
                  >
                    <CalendarImport onComplete={() => toast.success("Gigs imported! Check your Gigs page.")} />
                  </IntegrationRow>
                )}

                <IntegrationRow
                  icon={Rss}
                  title="Calendar feed"
                  helpText="Paste this link into Apple Calendar, Google Calendar, Outlook or any app that supports calendar subscriptions to follow your Fretless gigs. It updates automatically whenever a gig changes — no manual re-import."
                  testId="ical-feed-section"
                >
                  {feedToken ? (
                    <div style={{ display: "flex", gap: tokens.space.sm }}>
                      <Input
                        readOnly
                        value={(() => { const base = API.startsWith('http') ? API : window.location.origin + API; return `webcal://${new URL(base).host}/api/calendar/feed/${feedToken}`; })()}
                        className="text-xs font-mono"
                        style={{ background: tokens.color.cardBg }}
                        data-testid="ical-feed-url"
                      />
                      <GhostAction
                        onClick={async () => {
                          const feedBase = API.startsWith('http') ? API : window.location.origin + API;
                          await copyToClipboard(`${feedBase}/calendar/feed/${feedToken}`);
                          setFeedCopied(true); toast.success("Copied!"); setTimeout(() => setFeedCopied(false), 2000);
                        }}
                        testId="copy-feed-btn"
                        icon={feedCopied ? Check : Copy}
                      >
                        {feedCopied ? "Copied" : "Copy"}
                      </GhostAction>
                    </div>
                  ) : (
                    <p style={{ margin: 0, ...tokens.type.bodySm, color: tokens.color.textDim }}>Loading feed…</p>
                  )}
                </IntegrationRow>

                {/* Travel time in calendar events */}
                <IntegrationRow
                  icon={Car}
                  title="Travel time in calendar events"
                  helpText="Shifts the calendar event's start back by your estimated journey time, so your calendar blocks the full door-to-door trip. Applies to both Google Calendar sync and the calendar feed."
                  testId="calendar-include-travel-section"
                  actions={
                    <label style={{ display: "flex", alignItems: "center", gap: tokens.space.sm, cursor: "pointer" }}>
                      <span style={{ fontSize: 13, color: tokens.color.textDim, userSelect: "none" }}>
                        {calendarIncludeTravel ? "On" : "Off"}
                      </span>
                      <div
                        onClick={async () => {
                          const next = !calendarIncludeTravel;
                          setCalendarIncludeTravel(next);
                          try {
                            await apiClient.put(`/calendar/settings?include_travel_in_calendar=${next}`);
                            toast.success(next
                              ? "Calendar events will now include travel time"
                              : "Calendar events will start at call/show time");
                          } catch {
                            setCalendarIncludeTravel(!next);
                            toast.error("Failed to update setting");
                          }
                        }}
                        data-testid="calendar-include-travel-toggle"
                        role="switch"
                        aria-checked={calendarIncludeTravel}
                        style={{
                          width: 40, height: 22, borderRadius: 11,
                          background: calendarIncludeTravel ? tokens.color.orange : tokens.color.innerBg,
                          border: `1px solid ${calendarIncludeTravel ? tokens.color.orange : tokens.color.textDim}`,
                          position: "relative", cursor: "pointer",
                          transition: `background 150ms ease, border-color 150ms ease`,
                          flexShrink: 0,
                        }}
                      >
                        <span style={{
                          position: "absolute", top: 3,
                          left: calendarIncludeTravel ? 21 : 3,
                          width: 14, height: 14, borderRadius: "50%",
                          background: calendarIncludeTravel ? tokens.color.onOrange : tokens.color.textDim,
                          transition: `left 150ms ease`,
                        }} />
                      </div>
                    </label>
                  }
                />
            </div>

            {/* ── ACCOUNTING ── */}
            {/* Title-only rows; the shared "push invoices / auto-record
                payments" explainer lives behind each row's ? (invisible by
                default, so repeating it per platform costs nothing). The
                connected company name stays visible — it's status, not copy. */}
            <div style={{ display: "flex", flexDirection: "column", gap: tokens.space.md }}>
              <SectionHeading>Accounting</SectionHeading>
              <IntegrationRow
                icon={Receipt}
                title="FreeAgent"
                description={freeagentConnected ? (freeagentCompanyName || null) : null}
                helpText="Push invoices to FreeAgent straight from the Money tab. Marking an invoice paid in Fretless records a matching payment automatically."
                testId="freeagent-section"
                connected={freeagentConnected}
                actions={freeagentConnected ? (
                  <GhostAction onClick={disconnectFreeagent} loading={freeagentLoading} testId="disconnect-freeagent">
                    Disconnect
                  </GhostAction>
                ) : (
                  <PrimaryAction onClick={connectFreeagent} loading={freeagentLoading} icon={Link2} testId="connect-freeagent">
                    Connect
                  </PrimaryAction>
                )}
              >
                {freeagentConnected && (
                  <BankAccountPicker
                    platform="FreeAgent"
                    accounts={freeagentBankAccounts}
                    value={freeagentDefaultBank}
                    onChange={(id) => saveDefaultBank("freeagent", id)}
                    testId="freeagent-bank-account"
                  />
                )}
              </IntegrationRow>
              <IntegrationRow
                icon={Receipt}
                title="Xero"
                description={xeroConnected ? (xeroCompanyName || null) : null}
                helpText="Push invoices to Xero straight from the Money tab. Marking an invoice paid in Fretless records a matching payment automatically."
                testId="xero-section"
                connected={xeroConnected}
                actions={xeroConnected ? (
                  <GhostAction onClick={disconnectXero} loading={xeroLoading} testId="disconnect-xero">
                    Disconnect
                  </GhostAction>
                ) : (
                  <PrimaryAction onClick={connectXero} loading={xeroLoading} icon={Link2} testId="connect-xero">
                    Connect
                  </PrimaryAction>
                )}
              >
                {xeroConnected && (
                  xeroBankError ? (
                    <div style={{ ...tokens.type.bodySm, color: tokens.color.orange }} data-testid="xero-bank-error">
                      {xeroBankError}
                    </div>
                  ) : (
                    <BankAccountPicker
                      platform="Xero"
                      accounts={xeroBankAccounts}
                      value={xeroDefaultBank}
                      onChange={(id) => saveDefaultBank("xero", id)}
                      testId="xero-bank-account"
                    />
                  )
                )}
              </IntegrationRow>
              <IntegrationRow
                icon={Receipt}
                title="QuickBooks"
                description={quickbooksConnected ? (quickbooksCompanyName || null) : null}
                helpText="Push invoices to QuickBooks straight from the Money tab. Marking an invoice paid in Fretless records a matching payment automatically."
                testId="quickbooks-section"
                connected={quickbooksConnected}
                actions={quickbooksConnected ? (
                  <GhostAction onClick={disconnectQuickbooks} loading={quickbooksLoading} testId="disconnect-quickbooks">
                    Disconnect
                  </GhostAction>
                ) : (
                  <PrimaryAction onClick={connectQuickbooks} loading={quickbooksLoading} icon={Link2} testId="connect-quickbooks">
                    Connect
                  </PrimaryAction>
                )}
              >
                {quickbooksConnected && (
                  quickbooksBankError ? (
                    <div style={{ ...tokens.type.bodySm, color: tokens.color.orange }} data-testid="quickbooks-bank-error">
                      {quickbooksBankError}
                    </div>
                  ) : (
                    <BankAccountPicker
                      platform="QuickBooks"
                      accounts={quickbooksBankAccounts}
                      value={quickbooksDefaultBank}
                      onChange={(id) => saveDefaultBank("quickbooks", id)}
                      testId="quickbooks-bank-account"
                    />
                  )
                )}
              </IntegrationRow>
            </div>

            {/* ── IMPORT GIGS ── */}
            {/* No intro paragraph — it just enumerated the three row titles
                below it. Each row's ? help carries the detail. */}
            <div style={{ display: "flex", flexDirection: "column", gap: tokens.space.md }}>
              <SectionHeading>Import Gigs</SectionHeading>
              <div style={{ display: "flex", flexDirection: "column", gap: tokens.space.md }}>
                {/* CSV Import */}
                <IntegrationRow
                  icon={Upload}
                  title="CSV file"
                  helpText="Bulk import from a spreadsheet. Upload a .csv, .tsv or .txt file with one gig per row. Our AI auto-detects which columns are dates, venues, fees etc. — no need to match a strict template. After upload you'll see a preview where you can deselect any duplicates before confirming the import. Use the Template button to download a starter spreadsheet."
                  testId="csv-import-section"
                  actions={!csvPreview && (
                    <>
                      <input
                        type="file"
                        accept=".csv,.tsv,.txt"
                        id="csv-upload"
                        className="hidden"
                        data-testid="csv-file-input"
                        onChange={async (e) => {
                          const file = e.target.files?.[0]; if (!file) return;
                          setCsvLoading(true);
                          try {
                            const form = new FormData(); form.append("file", file);
                            const res = await apiClient.post("/gigs/import/csv-preview", form, { headers: { "Content-Type": "multipart/form-data" } });
                            setCsvPreview(res.data);
                            const ids = new Set(); res.data.previews?.forEach((p, i) => { if (!p.is_duplicate) ids.add(i); }); setCsvSelected(ids);
                          } catch (err) { toast.error(err.response?.data?.detail || "Failed to parse CSV"); } finally { setCsvLoading(false); }
                        }}
                      />
                      <PrimaryAction
                        onClick={() => document.getElementById("csv-upload").click()}
                        loading={csvLoading}
                        icon={Upload}
                        testId="csv-upload-btn"
                      >
                        Upload CSV
                      </PrimaryAction>
                      <GhostAction
                        onClick={() => window.open('/api/gigs/import/csv-template', '_blank')}
                        icon={Download}
                        testId="csv-template-btn"
                      >
                        Template
                      </GhostAction>
                    </>
                  )}
                >
                  {csvPreview && (
                    <div style={{ display: "flex", flexDirection: "column", gap: tokens.space.sm }}>
                      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: tokens.space.sm }}>
                        <p style={{ margin: 0, ...tokens.type.bodySm, color: tokens.color.text, fontWeight: 500 }}>
                          {csvPreview.total} rows
                          <span style={{ color: tokens.color.textMuted, fontWeight: 400 }}> ({csvPreview.duplicates} duplicates)</span>
                        </p>
                        <div style={{ display: "flex", gap: tokens.space.sm }}>
                          <GhostAction onClick={() => setCsvPreview(null)}>Cancel</GhostAction>
                          <PrimaryAction
                            onClick={async () => {
                              setCsvLoading(true);
                              try {
                                const gigs = csvPreview.previews.filter((_, i) => csvSelected.has(i));
                                const res = await apiClient.post("/gigs/import/csv-confirm", { gigs });
                                toast.success(`Imported ${res.data.imported} gig(s)!`); setCsvPreview(null);
                              } catch { toast.error("Import failed"); } finally { setCsvLoading(false); }
                            }}
                            loading={csvLoading}
                            disabled={csvSelected.size === 0}
                            testId="csv-confirm-btn"
                          >
                            Import {csvSelected.size}
                          </PrimaryAction>
                        </div>
                      </div>
                      <div style={{ maxHeight: 200, overflowY: "auto", display: "flex", flexDirection: "column", gap: 2 }}>
                        {csvPreview.previews?.slice(0, 20).map((row, i) => (
                          <label
                            key={i}
                            style={{
                              display: "flex",
                              alignItems: "center",
                              gap: tokens.space.sm,
                              padding: 6,
                              borderRadius: tokens.radius.sm,
                              ...tokens.type.bodySm,
                              fontSize: 12,
                              color: csvSelected.has(i) ? tokens.color.text : tokens.color.textMuted,
                              background: csvSelected.has(i) ? tokens.color.orangeSoft : "transparent",
                              cursor: "pointer",
                              opacity: csvSelected.has(i) ? 1 : 0.7,
                            }}
                          >
                            <input
                              type="checkbox"
                              checked={csvSelected.has(i)}
                              onChange={() => { setCsvSelected(prev => { const n = new Set(prev); n.has(i) ? n.delete(i) : n.add(i); return n; }); }}
                              style={{ accentColor: tokens.color.orange }}
                            />
                            <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", flex: 1 }}>
                              {row.artist_name || '?'} @ {row.venue_name || '?'} — {row.date || '?'}
                            </span>
                            {row.is_duplicate && (
                              <span style={{
                                fontFamily: tokens.font.mono, fontSize: 9, fontWeight: 600,
                                color: tokens.color.amber,
                                background: tokens.color.amberSoft,
                                padding: "1px 5px", borderRadius: tokens.radius.sm,
                              }}>DUP</span>
                            )}
                          </label>
                        ))}
                      </div>
                    </div>
                  )}
                </IntegrationRow>

                {/* ICS / iCal Import */}
                <IntegrationRow
                  icon={Calendar}
                  title="ICS / iCal file"
                  helpText="Export your gigs as an .ics file from Apple Calendar, Google Calendar, Outlook, or any other calendar that supports iCal exports — then upload it here to bring those events in as Fretless gigs. One-time bulk import, unlike the Calendar feed above which is a live feed in the opposite direction."
                  testId="ics-import-section"
                >
                  <IcsImport onComplete={() => toast.success("Gigs imported! Check your Gigs page.")} />
                </IntegrationRow>

                {/* Songkick Import */}
                <IntegrationRow
                  icon={Music}
                  title="Songkick profile"
                  helpText="Paste the URL of your artist profile on songkick.com — we'll pull the public gig list and let you preview before importing. Note: Songkick lists concerts only, not festivals, and venue names must match Songkick's database for the import to succeed."
                  testId="songkick-import-section"
                  actions={!songkickResults && (
                    <PrimaryAction
                      onClick={async () => {
                        if (!songkickUrl) return;
                        setSongkickLoading(true);
                        try { const res = await apiClient.post("/gigs/import/songkick", { url: songkickUrl }); setSongkickResults(res.data); }
                        catch (err) { toast.error(err.response?.data?.detail || "Failed to scrape Songkick"); }
                        finally { setSongkickLoading(false); }
                      }}
                      loading={songkickLoading}
                      disabled={!songkickUrl}
                      testId="songkick-scan-btn"
                    >
                      Scan
                    </PrimaryAction>
                  )}
                >
                  {!songkickResults ? (
                    <Input
                      value={songkickUrl}
                      onChange={(e) => setSongkickUrl(e.target.value)}
                      placeholder="https://www.songkick.com/artists/..."
                      className="text-xs"
                      data-testid="songkick-url-input"
                    />
                  ) : (
                    <div style={{ display: "flex", flexDirection: "column", gap: tokens.space.sm }}>
                      <p style={{ margin: 0, ...tokens.type.bodySm, color: tokens.color.text }}>
                        {songkickResults.total} gigs found
                        <span style={{ color: tokens.color.textMuted }}> ({songkickResults.duplicates} duplicates)</span>
                      </p>
                      <div style={{ display: "flex", gap: tokens.space.sm }}>
                        <GhostAction onClick={() => setSongkickResults(null)}>Cancel</GhostAction>
                        <PrimaryAction
                          onClick={async () => {
                            setSongkickLoading(true);
                            try {
                              const gigs = songkickResults.gigs.filter(g => !g.is_duplicate);
                              const res = await apiClient.post("/gigs/import/csv-confirm", { gigs });
                              toast.success(`Imported ${res.data.imported} gig(s) from Songkick!`); setSongkickResults(null);
                            } catch { toast.error("Import failed"); } finally { setSongkickLoading(false); }
                          }}
                          loading={songkickLoading}
                          testId="songkick-import-btn"
                        >
                          Import {songkickResults.gigs?.filter(g => !g.is_duplicate).length || 0}
                        </PrimaryAction>
                      </div>
                    </div>
                  )}
                </IntegrationRow>
              </div>
            </div>

            {/* ── EXPORT GIGS ── */}
            <div style={{ display: "flex", flexDirection: "column", gap: tokens.space.md }}>
              <SectionHeading>Export Gigs</SectionHeading>
              <div style={{ display: "flex", flexDirection: "column", gap: tokens.space.md }}>
                <ExportCard
                  title="Bandsintown CSV"
                  helpText="Downloads a CSV of your gigs formatted for Bandsintown. Upload it via Bandsintown for Artists → Events → Upload."
                  icon={<Download style={{ width: 18, height: 18, color: tokens.color.textDim }} />}
                  endpoint="/gigs/export/csv?format=bandsintown"
                  filename="bandsintown"
                  testId="export-bandsintown"
                />
                <ExportCard
                  title="Songkick CSV"
                  helpText="Downloads a CSV of your gigs formatted for Songkick. Upload it via Songkick Tourbox → Import shows."
                  icon={<Download style={{ width: 18, height: 18, color: tokens.color.textDim }} />}
                  endpoint="/gigs/export/csv?format=songkick"
                  filename="songkick"
                  testId="export-songkick"
                />
              </div>
            </div>

            {/* ── SOCIAL ── */}
            <div style={{ display: "flex", flexDirection: "column", gap: tokens.space.md }}>
              <SectionHeading>Social</SectionHeading>
              <IntegrationRow
                icon={Instagram}
                title="Instagram / Facebook / X"
                helpText="Post gig announcements directly to your social accounts from Fretless."
                comingSoon
                actions={<ComingSoonBadge />}
              />
            </div>
          </div>
        )}
        {/* ACCOUNT SETTINGS */}
        {activeTab === "account" && (
          <div className="space-y-6">
            {/* Change Password Card */}
            <Card>
              <CardHeader>
                <CardTitle>Change Password</CardTitle>
                <CardDescription>
                  Update your account password
                </CardDescription>
              </CardHeader>
              <CardContent>
                <form onSubmit={changePassword} className="space-y-4 max-w-md">
                  <div className="space-y-2">
                    <Label htmlFor="current_password">Current Password</Label>
                    <div className="relative">
                      <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                      <Input
                        id="current_password"
                        type={showPasswords ? "text" : "password"}
                        className="pl-10"
                        value={passwordData.current}
                        onChange={(e) => setPasswordData({ ...passwordData, current: e.target.value })}
                        data-testid="current-password"
                      />
                    </div>
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="new_password">New Password</Label>
                    <div className="relative">
                      <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                      <Input
                        id="new_password"
                        type={showPasswords ? "text" : "password"}
                        className="pl-10 pr-10"
                        value={passwordData.new}
                        onChange={(e) => setPasswordData({ ...passwordData, new: e.target.value })}
                        data-testid="new-password"
                      />
                      <button
                        type="button"
                        onClick={() => setShowPasswords(!showPasswords)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                      >
                        {showPasswords ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                      </button>
                    </div>
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="confirm_password">Confirm New Password</Label>
                    <div className="relative">
                      <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                      <Input
                        id="confirm_password"
                        type={showPasswords ? "text" : "password"}
                        className="pl-10"
                        value={passwordData.confirm}
                        onChange={(e) => setPasswordData({ ...passwordData, confirm: e.target.value })}
                        data-testid="confirm-password"
                      />
                    </div>
                  </div>
                  
                  <Button type="submit" disabled={saving} data-testid="change-password">
                    {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}
                    Change Password
                  </Button>
                </form>
              </CardContent>
            </Card>
            
            {/* Danger Zone */}
            <Card className="border-red-500/30">
              <CardHeader>
                <CardTitle className="text-red-500 flex items-center gap-2">
                  <AlertTriangle className="h-5 w-5" /> Danger Zone
                </CardTitle>
                <CardDescription>Irreversible actions. Proceed with caution.</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <Button 
                  variant="outline" 
                  className="text-red-500 border-red-500/50 hover:bg-red-500/10"
                  onClick={logout}
                  data-testid="logout"
                >
                  <LogOut className="h-4 w-4 mr-2" />
                  Sign Out
                </Button>
                <div className="border-t border-red-500/20 pt-4">
                  <p className="text-sm text-neutral-400 mb-3">Permanently delete your account and all data. This cannot be undone.</p>
                  <Button 
                    variant="destructive"
                    onClick={() => setShowDeleteModal(true)}
                    data-testid="delete-account-btn"
                  >
                    <Trash2 className="h-4 w-4 mr-2" />
                    Delete my account
                  </Button>
                </div>
              </CardContent>
            </Card>

            {/* Delete Account Modal */}
            {showDeleteModal && (
              <div className="fixed inset-0 z-[100] bg-black/70 backdrop-blur-sm flex items-center justify-center p-4" role="dialog" aria-label="Delete account confirmation">
                <div className="max-w-md w-full bg-card rounded-xl p-6 border border-destructive/30" data-testid="delete-account-modal">
                  <h2 className="text-lg font-bold text-red-500 mb-2">Permanently delete your account?</h2>
                  <p className="text-sm text-neutral-400 mb-4">
                    This will permanently delete your account, all your gigs, setlists, invoices, and data. This cannot be undone.
                  </p>
                  <div className="space-y-3">
                    <Label className="text-sm text-neutral-300">Type <strong className="text-red-400">DELETE</strong> to confirm</Label>
                    <Input
                      value={deleteConfirmText}
                      onChange={(e) => setDeleteConfirmText(e.target.value)}
                      className="border-neutral-700"
                      autoComplete="off"
                      data-testid="delete-confirm-input"
                    />
                    <div className="flex gap-3 pt-2">
                      <Button
                        variant="outline"
                        onClick={() => { setShowDeleteModal(false); setDeleteConfirmText(""); }}
                        className="flex-1"
                        data-testid="delete-cancel-btn"
                      >
                        Cancel
                      </Button>
                      <Button
                        variant="destructive"
                        disabled={deleteConfirmText !== "DELETE" || deleting}
                        onClick={handleDeleteAccount}
                        className="flex-1"
                        data-testid="delete-confirm-btn"
                      >
                        {deleting ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Trash2 className="h-4 w-4 mr-2" />}
                        Delete my account
                      </Button>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Developer Tools — admin only */}
            {user?.is_admin && (
              <Card className="border-neutral-700/50 mt-6" data-testid="developer-tools-section">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2 text-neutral-300 text-base">
                    <Wrench className="h-4 w-4" /> Developer Tools
                  </CardTitle>
                  <CardDescription>Admin-only testing tools.</CardDescription>
                </CardHeader>
                <CardContent>
                  <Button 
                    variant="outline"
                    size="sm"
                    disabled={resettingPrompt}
                    onClick={handleResetInstallPrompt}
                    data-testid="reset-install-prompt-btn"
                  >
                    {resettingPrompt ? (
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    ) : (
                      <RotateCcw className="h-4 w-4 mr-2" />
                    )}
                    Reset install prompt
                  </Button>
                </CardContent>
              </Card>
            )}
          </div>
        )}
        
        {/* PROFILE */}
        {activeTab === "profile" && (
          <div className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle>Profile</CardTitle>
                <CardDescription>
                  Your account information
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-center gap-4">
                  {user?.picture ? (
                    <img 
                      src={user.picture} 
                      alt={user.name} 
                      className="h-16 w-16 rounded-full"
                    />
                  ) : (
                    <div className="h-16 w-16 rounded-full bg-primary/10 flex items-center justify-center">
                      <User className="h-8 w-8 text-primary" />
                    </div>
                  )}
                  <div>
                    <h3 className="font-heading text-xl font-semibold">{user?.name || "User"}</h3>
                    <p className="text-muted-foreground flex items-center gap-2">
                      <Mail className="h-4 w-4" />
                      {user?.email || "No email"}
                    </p>
                  </div>
                </div>
                
                <div className="pt-4 border-t">
                  <p className="text-sm text-muted-foreground">
                    Account created via {user?.picture ? "Google" : "Email"}
                  </p>
                </div>
              </CardContent>
            </Card>

            {/* Social Handles for AI Tone */}
            <Card>
              <CardHeader>
                <CardTitle>Your Artist Name(s)</CardTitle>
                <CardDescription>
                  Used by AI to understand your role in each gig — bandleader, featured guest, or sideman.
                </CardDescription>
              </CardHeader>
              <CardContent>
                <Input
                  value={artistNames}
                  onChange={(e) => setArtistNames(e.target.value)}
                  data-testid="artist-names-input"
                />
                <p className="text-xs text-muted-foreground mt-2">
                  This helps Smart Fill and Social Generator write from the right perspective.
                </p>
              </CardContent>
            </Card>

            {/* Public Profile & QR Code */}
            <Card>
              <CardHeader>
                <CardTitle>Public Profile & QR Code</CardTitle>
                <CardDescription>
                  Set a username for your public profile link. Share your QR code at gigs to connect with other musicians.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="username">Username</Label>
                  <div className="flex gap-2">
                    <Input
                      id="username"
                      value={username}
                      onChange={(e) => setUsername(e.target.value.toLowerCase().replace(/[^a-z0-9_-]/g, ''))}
                      data-testid="username-input"
                    />
                    <Button
                      size="sm"
                      disabled={usernameSaving || !username || username.length < 3}
                      onClick={async () => {
                        setUsernameSaving(true);
                        try {
                          await apiClient.put("/user/username", { username });
                          toast.success("Username saved!");
                        } catch (e) {
                          toast.error(e.response?.data?.detail || "Failed to save username");
                        } finally {
                          setUsernameSaving(false);
                        }
                      }}
                      data-testid="save-username-btn"
                    >
                      {usernameSaving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save"}
                    </Button>
                  </div>
                  {username && username.length >= 3 && (
                    <div className="flex items-center gap-2 text-xs text-muted-foreground">
                      <span>
                        Your profile: <a href={`/musician/${username}`} className="text-primary hover:underline">fretless.life/musician/{username}</a>
                      </span>
                      <button
                        type="button"
                        className="inline-flex items-center gap-1 text-primary hover:underline"
                        onClick={() => {
                          navigator.clipboard.writeText(`${window.location.origin}/musician/${username}`);
                          setProfileLinkCopied(true);
                          toast.success("Profile link copied!");
                          setTimeout(() => setProfileLinkCopied(false), 2000);
                        }}
                        data-testid="copy-profile-link-btn"
                      >
                        {profileLinkCopied ? <Check className="h-3 w-3" /> : <Copy className="h-3 w-3" />}
                        {profileLinkCopied ? "Copied" : "Copy"}
                      </button>
                    </div>
                  )}
                </div>
                
                {username && username.length >= 3 && (
                  <div className="flex flex-col items-center gap-3 pt-3 border-t border-border">
                    <div className="bg-white p-3 rounded-xl" data-testid="qr-code">
                      <QRCodeSVG
                        value={`${window.location.origin}/musician/${username}`}
                        size={160}
                        fgColor="#0D0D0D"
                        bgColor="#FFFFFF"
                        level="M"
                      />
                    </div>
                    <p className="text-xs text-muted-foreground text-center">
                      Scan to view your musician profile
                    </p>
                  </div>
                )}
              </CardContent>
            </Card>

            <ReferralCard username={username} />

            <WebsiteWidgetCard username={username} />

            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  Social Media Handles
                </CardTitle>
                <CardDescription>
                  Add your social handles to enable "My Style" tone in the Social Generator. 
                  The AI will analyze your recent posts to match your unique voice.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid gap-4 md:grid-cols-3">
                  <div className="space-y-2">
                    <Label htmlFor="instagram_handle" className="flex items-center gap-2">
                      <Instagram className="h-4 w-4 text-pink-500" /> Instagram
                    </Label>
                    <div className="relative">
                      <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground">@</span>
                      <Input
                        id="instagram_handle"
                        className="pl-8"
                        value={socialHandles.instagram_handle}
                        onChange={(e) => setSocialHandles({ ...socialHandles, instagram_handle: e.target.value.replace('@', '') })}
                        data-testid="instagram-handle"
                      />
                    </div>
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="twitter_handle" className="flex items-center gap-2">
                      <XLogo className="h-4 w-4" /> X (Twitter)
                    </Label>
                    <div className="relative">
                      <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground">@</span>
                      <Input
                        id="twitter_handle"
                        className="pl-8"
                        value={socialHandles.twitter_handle}
                        onChange={(e) => setSocialHandles({ ...socialHandles, twitter_handle: e.target.value.replace('@', '') })}
                        data-testid="twitter-handle"
                      />
                    </div>
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="facebook_handle" className="flex items-center gap-2">
                      <Facebook className="h-4 w-4 text-blue-600" /> Facebook
                    </Label>
                    <Input
                      id="facebook_handle"
                      value={socialHandles.facebook_handle}
                      onChange={(e) => setSocialHandles({ ...socialHandles, facebook_handle: e.target.value })}
                      data-testid="facebook-handle"
                    />
                  </div>
                </div>

                {/* My Website — shown on the public profile alongside socials.
                    Saved with the same "Save Profile" button below. */}
                <div className="space-y-2">
                  <Label htmlFor="website" className="flex items-center gap-2">
                    <Globe className="h-4 w-4 text-primary" /> My Website
                  </Label>
                  <Input
                    id="website"
                    type="url"
                    inputMode="url"
                    placeholder="yourband.com"
                    value={website}
                    onChange={(e) => setWebsite(e.target.value)}
                    data-testid="website-input"
                  />
                  <p className="text-xs text-muted-foreground">
                    Appears on your public profile. We'll add https:// automatically.
                  </p>
                </div>

                <div className="flex justify-end pt-2">
                  <Button onClick={saveSocialHandles} disabled={saving} data-testid="save-social-handles">
                    {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}
                    Save Profile
                  </Button>
                </div>
                
                {styleExamples.length > 0 && (
                  <div className="p-3 bg-emerald-500/10 rounded-lg border border-emerald-500/20 text-sm">
                    <p className="text-emerald-400">
                      ✓ "My Style" tone will match your voice based on {styleExamples.length} example{styleExamples.length !== 1 ? 's' : ''}!
                    </p>
                  </div>
                )}
              </CardContent>
            </Card>
            
            {/* Style Examples for AI Voice Matching */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  Your Writing Style
                </CardTitle>
                <CardDescription>
                  Paste 3-5 examples of posts you've written that you like. The AI will analyze your vocabulary, 
                  tone, and personality to generate posts that sound authentically like YOU.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* Existing Examples */}
                {styleExamples.length > 0 && (
                  <div className="space-y-3">
                    <Label>Your Example Posts ({styleExamples.length}/5)</Label>
                    {styleExamples.map((example, index) => (
                      <div key={index} className="relative p-3 bg-secondary/30 rounded-lg border border-border">
                        <p className="text-sm pr-8 whitespace-pre-wrap">{example}</p>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="absolute top-2 right-2 h-6 w-6 text-muted-foreground hover:text-destructive"
                          onClick={() => removeStyleExample(index)}
                          data-testid={`remove-example-${index}`}
                        >
                          ×
                        </Button>
                      </div>
                    ))}
                  </div>
                )}
                
                {/* Add New Example */}
                {styleExamples.length < 5 && (
                  <div className="space-y-2">
                    <Label htmlFor="new_example">
                      {styleExamples.length === 0 ? "Add your first example" : "Add another example"}
                    </Label>
                    <Textarea
                      id="new_example"
                      value={newExample}
                      onChange={(e) => setNewExample(e.target.value)}
                      className="min-h-[100px]"
                      data-testid="new-style-example"
                    />
                    <div className="flex justify-between items-center">
                      <p className="text-xs text-muted-foreground">
                        {1000 - newExample.length} characters remaining
                      </p>
                      <Button 
                        variant="outline" 
                        size="sm"
                        onClick={addStyleExample}
                        disabled={!newExample.trim() || newExample.length > 1000}
                        data-testid="add-example"
                      >
                        Add Example
                      </Button>
                    </div>
                  </div>
                )}
                
                {styleExamples.length === 5 && (
                  <p className="text-sm text-muted-foreground text-center py-2">
                    Maximum 5 examples reached. Remove one to add another.
                  </p>
                )}
                
                {styleExamples.length > 0 && (
                  <div className="pt-2 border-t">
                    <p className="text-xs text-muted-foreground">
                      💡 Tip: Include posts with different vibes - excited gig announcements, thoughtful reflections, 
                      casual updates. This helps the AI understand your full range.
                    </p>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        )}

        {/* PLAN / SUBSCRIPTION */}
        {activeTab === "plan" && (
          <div className="space-y-6">
            {upgradedBanner && (
              <div className="bg-primary/10 border border-primary/30 rounded-lg px-4 py-3 flex items-center gap-3" data-testid="upgraded-banner">
                <Crown className="h-5 w-5 text-primary" />
                <span className="text-sm font-medium text-primary">You're now on Pro — enjoy full access.</span>
              </div>
            )}
            <PricingCard subscription={user?.subscription} />
          </div>
        )}
        {/* ADMIN USER MANAGEMENT (admin-only) */}
        {user?.is_admin && activeTab === "admin" && (
          <>
            <Card>
              <CardHeader
                onClick={() => setUsersListExpanded(!usersListExpanded)}
                className="cursor-pointer hover:bg-secondary/30 transition-colors flex items-center justify-between"
              >
                <div className="flex-1">
                  <CardTitle className="flex items-center gap-2">
                    <Users className="h-5 w-5" /> User Management
                  </CardTitle>
                  <CardDescription>Manage all registered users.</CardDescription>
                </div>
                <ChevronRight
                  className={`h-5 w-5 text-muted-foreground transition-transform ${usersListExpanded ? 'rotate-90' : ''}`}
                />
              </CardHeader>
              {usersListExpanded && (
              <CardContent>
                {adminLoading ? (
                  <div className="flex justify-center py-8"><Loader2 className="h-6 w-6 animate-spin" /></div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm" data-testid="admin-users-table">
                      <thead>
                        <tr className="border-b border-border text-left text-muted-foreground">
                          <th className="py-2 pr-4 w-8">
                            <Checkbox
                              checked={selectedUsers.size === adminUsers.length && adminUsers.length > 0}
                              onCheckedChange={(checked) => handleSelectAllUsers(checked)}
                              data-testid="select-all-users"
                            />
                          </th>
                          <th className="py-2 pr-4">Email</th>
                          <th className="py-2 pr-4">Plan</th>
                          <th className="py-2 pr-4 hidden md:table-cell">Signed up</th>
                          <th className="py-2 pr-4 hidden md:table-cell">Gigs</th>
                          <th className="py-2 pr-4 hidden md:table-cell">Install prompt</th>
                          <th className="py-2"></th>
                        </tr>
                      </thead>
                      <tbody>
                        {adminUsers.map((u) => (
                          <tr key={u.user_id} className={`border-b border-border/50 ${selectedUsers.has(u.user_id) ? 'bg-secondary/50' : ''}`}>
                            <td className="py-3 pr-4">
                              <Checkbox
                                checked={selectedUsers.has(u.user_id)}
                                onCheckedChange={() => toggleUserSelection(u.user_id)}
                                data-testid={`select-user-${u.email}`}
                              />
                            </td>
                            <td className="py-3 pr-4 text-foreground">{u.email}</td>
                            <td className="py-3 pr-4">
                              <Badge variant={u.plan_type === "pro" || u.lifetime_access ? "default" : "secondary"} className="text-xs">
                                {u.lifetime_access ? "Lifetime" : u.plan_type || "free"}
                              </Badge>
                            </td>
                            <td className="py-3 pr-4 hidden md:table-cell text-muted-foreground">
                              {u.created_at ? new Date(u.created_at).toLocaleDateString('en-GB') : '-'}
                            </td>
                            <td className="py-3 pr-4 hidden md:table-cell text-muted-foreground">{u.gig_count ?? 0}</td>
                            <td className="py-3 pr-4 hidden md:table-cell" data-testid={`prompt-status-${u.email}`}>
                              {u.has_seen_install_prompt ? (
                                <Check className="h-4 w-4 text-green-500" />
                              ) : (
                                <Minus className="h-4 w-4 text-muted-foreground" />
                              )}
                            </td>
                            <td className="py-3 text-right whitespace-nowrap">
                              <Button
                                variant="ghost"
                                size="sm"
                                className="text-muted-foreground hover:text-foreground mr-1"
                                onClick={() => handleAdminResetPrompt(u.user_id, u.email)}
                                disabled={adminResettingPrompt === u.user_id}
                                data-testid={`admin-reset-prompt-${u.email}`}
                              >
                                {adminResettingPrompt === u.user_id ? (
                                  <Loader2 className="h-4 w-4 animate-spin" />
                                ) : (
                                  <RotateCcw className="h-4 w-4" />
                                )}
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                className="text-red-500 hover:text-red-400 hover:bg-red-500/10"
                                onClick={() => setAdminDeleteTarget(u)}
                                data-testid={`admin-delete-${u.email}`}
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                    {adminUsers.length === 0 && (
                      <p className="text-center text-muted-foreground py-8">No users found</p>
                    )}
                  </div>
                )}
              </CardContent>
              )}

              {/* Bulk Actions */}
              {usersListExpanded && selectedUsers.size > 0 && (
                <div className="px-6 py-4 border-t border-border bg-secondary/50 flex items-center justify-between">
                  <span className="text-sm text-muted-foreground">
                    {selectedUsers.size} user(s) selected
                  </span>
                  <div className="flex gap-2">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setSelectedUsers(new Set())}
                      className="text-muted-foreground hover:text-foreground"
                    >
                      Clear
                    </Button>
                    <Button
                      size="sm"
                      onClick={handleBulkResetPrompt}
                      className="text-muted-foreground hover:text-foreground"
                      variant="ghost"
                    >
                      <RotateCcw className="h-4 w-4 mr-1" /> Reset Prompt (Bulk)
                    </Button>
                    <Button
                      size="sm"
                      onClick={handleBulkDeleteUsers}
                      variant="ghost"
                      className="text-red-500 hover:text-red-400 hover:bg-red-500/10"
                    >
                      <Trash2 className="h-4 w-4 mr-1" /> Delete Selected
                    </Button>
                  </div>
                </div>
              )}
            </Card>

            {/* Admin Tools */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
              <AdminVenueManager />
              <AdminDuplicateDetector />
              <AdminSmartFillTest />
              <AdminSubscribers />
              <AdminAnalytics />
              <AdminGrantLifetime />
              <AdminRecomputeTravelEstimates />
              {/* Landing page + gig invite page + invitation email previews
                  grouped together — all are "what a prospect/invitee sees
                  before signing up". Landing Page and Gig Invite Page link to
                  the real, live route (in a preview mode) rather than a
                  hand-rolled mockup, so they can't drift out of sync with the
                  actual design. */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Link2 className="h-5 w-5" /> Landing Page
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <Button onClick={() => navigate('/?preview=true')} variant="outline" className="w-full sm:w-auto">
                    View Landing Page
                  </Button>
                </CardContent>
              </Card>
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Eye className="h-5 w-5" /> Gig Invite Page
                  </CardTitle>
                  <CardDescription>What invitees see when they tap "View Gig & RSVP" in the invite email.</CardDescription>
                </CardHeader>
                <CardContent>
                  <Button onClick={() => navigate('/gig-preview/preview')} variant="outline" className="w-full sm:w-auto">
                    View Gig Invite Preview
                  </Button>
                </CardContent>
              </Card>
              <AdminInvitationEmailPreview />
            </div>

            {/* Admin Delete User Modal */}
            {adminDeleteTarget && (
              <div className="fixed inset-0 z-[100] bg-black/70 backdrop-blur-sm flex items-center justify-center p-4" role="dialog" aria-label="Admin delete user">
                <div className="max-w-md w-full bg-card rounded-xl p-6 border border-destructive/30" data-testid="admin-delete-modal">
                  <h2 className="text-lg font-bold text-red-500 mb-2">Delete user account?</h2>
                  <p className="text-sm text-neutral-400 mb-4">
                    This will permanently delete <strong className="text-white">{adminDeleteTarget.email}</strong> and all their data. A GDPR confirmation email will be sent to them.
                  </p>
                  <div className="flex gap-3">
                    <Button variant="outline" onClick={() => setAdminDeleteTarget(null)} className="flex-1" data-testid="admin-delete-cancel">
                      Cancel
                    </Button>
                    <Button
                      variant="destructive"
                      onClick={() => handleAdminDeleteUser(adminDeleteTarget.user_id, adminDeleteTarget.email)}
                      className="flex-1"
                      data-testid="admin-delete-confirm"
                    >
                      <Trash2 className="h-4 w-4 mr-2" />
                      Delete user
                    </Button>
                  </div>
                </div>
              </div>
            )}
          </>
        )}
      </>
    );

  return (
    <Layout>
    <div className="space-y-6">
      {/* Header — DS-token kicker + heroSm, matches other page intros */}
      <div style={{ marginBottom: tokens.space.xxl }}>
        <Kicker color={tokens.color.orange} style={{ marginBottom: tokens.space.xs }}>
          Account
        </Kicker>
        <h1
          style={{
            margin: 0,
            ...tokens.type.heroSm,
            color: tokens.color.text,
            textWrap: "balance",
          }}
        >
          Settings
        </h1>
        <p
          style={{
            margin: 0,
            marginTop: tokens.space.sm,
            ...tokens.type.body,
            color: tokens.color.textMuted,
          }}
        >
          Manage your account and preferences
        </p>
      </div>
      
      {/* Desktop: sidebar + content */}
      <div className="hidden md:flex gap-6">
        <nav
          className="w-[200px] shrink-0 self-start sticky top-4"
          data-testid="settings-sidebar"
          style={{
            background: tokens.color.innerBg,
            borderRadius: tokens.radius.lg,
            padding: tokens.space.sm,
          }}
        >
          {navItems.map(item => {
            const Icon = item.icon;
            const isActive = activeTab === item.value;
            return (
              <button
                key={item.value}
                onClick={() => handleNavClick(item.value)}
                data-testid={`tab-${item.value}`}
                style={{
                  width: "100%",
                  display: "flex",
                  alignItems: "center",
                  gap: tokens.space.md,
                  padding: "10px 12px",
                  borderRadius: tokens.radius.md,
                  textAlign: "left",
                  cursor: "pointer",
                  background: isActive ? "rgba(245, 166, 35, 0.10)" : "transparent",
                  color: isActive ? tokens.color.text : tokens.color.textDim,
                  borderTop: "none",
                  borderRight: "none",
                  borderBottom: "none",
                  borderLeftWidth: 2,
                  borderLeftStyle: "solid",
                  borderLeftColor: isActive ? tokens.color.orange : "transparent",
                  fontFamily: tokens.font.body,
                  fontSize: 14,
                  fontWeight: isActive ? 600 : 500,
                  transition: `color ${tokens.motion.fast}, background ${tokens.motion.fast}`,
                }}
                onMouseEnter={(e) => {
                  if (!isActive) {
                    e.currentTarget.style.color = tokens.color.text;
                    e.currentTarget.style.background = "rgba(255, 255, 255, 0.04)";
                  }
                }}
                onMouseLeave={(e) => {
                  if (!isActive) {
                    e.currentTarget.style.color = tokens.color.textDim;
                    e.currentTarget.style.background = "transparent";
                  }
                }}
              >
                <Icon style={{ width: 16, height: 16, color: isActive ? tokens.color.orange : "currentColor", flexShrink: 0 }} />
                {item.label}
              </button>
            );
          })}
          <div
            style={{
              marginTop: tokens.space.sm,
              paddingTop: tokens.space.sm,
              borderTop: tokens.border.hairline,
            }}
          >
            <button
              onClick={() => setShowOnboarding(true)}
              data-testid="tab-tour"
              style={{
                width: "100%",
                display: "flex",
                alignItems: "center",
                gap: tokens.space.md,
                padding: "10px 12px",
                borderRadius: tokens.radius.md,
                background: "transparent",
                color: tokens.color.textDim,
                border: "none",
                cursor: "pointer",
                fontFamily: tokens.font.body,
                fontSize: 14,
                fontWeight: 500,
                textAlign: "left",
                transition: `color ${tokens.motion.fast}, background ${tokens.motion.fast}`,
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.color = tokens.color.text;
                e.currentTarget.style.background = "rgba(255, 255, 255, 0.04)";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.color = tokens.color.textDim;
                e.currentTarget.style.background = "transparent";
              }}
            >
              <Music style={{ width: 16, height: 16, flexShrink: 0 }} />
              Tour the app
            </button>
          </div>
        </nav>
        <div className="flex-1 min-w-0">{renderContent()}</div>
      </div>

      {/* Mobile: list or content */}
      <div className="md:hidden">
        {!mobileShowContent ? (
          <div data-testid="settings-mobile-list" style={{ display: "flex", flexDirection: "column", gap: tokens.space.xs }}>
            {navItems.map(item => {
              const Icon = item.icon;
              return (
                <button
                  key={item.value}
                  onClick={() => handleNavClick(item.value)}
                  data-testid={`tab-${item.value}`}
                  style={{
                    width: "100%",
                    display: "flex",
                    alignItems: "center",
                    gap: tokens.space.lg,
                    padding: "14px 16px",
                    minHeight: 52,
                    borderRadius: tokens.radius.md,
                    background: tokens.color.innerBg,
                    border: tokens.border.inner,
                    cursor: "pointer",
                    textAlign: "left",
                  }}
                >
                  <Icon style={{ width: 18, height: 18, color: tokens.color.textMuted, flexShrink: 0 }} />
                  <span
                    style={{
                      flex: 1,
                      ...tokens.type.bodySm,
                      fontWeight: 500,
                      color: tokens.color.text,
                    }}
                  >
                    {item.label}
                  </span>
                  <ChevronRight style={{ width: 16, height: 16, color: tokens.color.textMuted }} />
                </button>
              );
            })}
            <div
              style={{
                marginTop: tokens.space.sm,
                paddingTop: tokens.space.sm,
                borderTop: tokens.border.hairline,
              }}
            >
              <button
                onClick={() => setShowOnboarding(true)}
                data-testid="tab-tour"
                style={{
                  width: "100%",
                  display: "flex",
                  alignItems: "center",
                  gap: tokens.space.lg,
                  padding: "14px 16px",
                  minHeight: 52,
                  borderRadius: tokens.radius.md,
                  background: tokens.color.innerBg,
                  border: tokens.border.inner,
                  cursor: "pointer",
                  textAlign: "left",
                }}
              >
                <Music style={{ width: 18, height: 18, color: tokens.color.textMuted, flexShrink: 0 }} />
                <span
                  style={{
                    flex: 1,
                    ...tokens.type.bodySm,
                    fontWeight: 500,
                    color: tokens.color.text,
                  }}
                >
                  Tour the app
                </span>
                <ChevronRight style={{ width: 16, height: 16, color: tokens.color.textMuted }} />
              </button>
            </div>
          </div>
        ) : (
          <div data-testid="settings-mobile-content">
            <button
              onClick={handleMobileBack}
              className="flex items-center gap-2 text-primary text-sm font-medium mb-4"
              data-testid="settings-mobile-back"
            >
              <ArrowLeft className="h-4 w-4" />
              Settings
            </button>
            {renderContent()}
          </div>
        )}
      </div>
    </div>
    {showOnboarding && (
      <OnboardingCarousel
        stepsComplete={user?.onboarding_steps_complete || Array(10).fill(false)}
        onToggle={(index) => {
          setUser(prev => {
            if (!prev) return prev;
            const current = prev.onboarding_steps_complete || Array(10).fill(false);
            const next = current.map((v, i) => i === index ? true : v);
            const allDone = next.every(Boolean);
            apiClient.patch("/user/profile", { onboarding_steps_complete: next }).catch(() => {});
            return { ...prev, onboarding_steps_complete: next, ...(allDone ? { onboarding_complete: true } : {}) };
          });
        }}
        onDismissAll={() => setShowOnboarding(false)}
      />
    )}
    </Layout>
  );
};

export default Settings;
